import PropTypes from 'prop-types';
import { Card, Grid, Stack, Tooltip, Typography } from '@mui/material';
import Iconify from '../../../components/Iconify';

const fEur = (n) =>
  new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

function KpiCard({ label, value, hint, icon, color = 'primary', deltas = [] }) {
  return (
    <Card variant="outlined" sx={{ p: 2.5, height: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Stack
          sx={{
            width: 48,
            height: 48,
            borderRadius: 1.5,
            bgcolor: `${color}.lighter`,
            color: `${color}.main`,
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Iconify icon={icon} width={24} height={24} />
        </Stack>
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="caption" color="text.secondary" noWrap>
              {label}
            </Typography>
            {hint && (
              <Tooltip title={hint}>
                <span style={{ display: 'inline-flex' }}>
                  <Iconify
                    icon="eva:info-outline"
                    width={14}
                    height={14}
                    sx={{ color: 'text.disabled', cursor: 'help' }}
                  />
                </span>
              </Tooltip>
            )}
          </Stack>
          <Typography variant="h4" noWrap>
            {value}
          </Typography>
          {deltas.map((d) => {
            const isUp = d.direction === 'up';
            const deltaColor = isUp ? 'success.main' : 'error.main';
            return (
              <Stack key={d.label} direction="row" spacing={0.25} alignItems="center" sx={{ width: 'fit-content' }}>
                <Iconify
                  icon={isUp ? 'eva:arrow-upward-fill' : 'eva:arrow-downward-fill'}
                  width={16}
                  height={16}
                  sx={{ color: deltaColor }}
                />
                <Typography variant="caption" sx={{ color: deltaColor, fontWeight: 700 }} noWrap>
                  {`${isUp ? '+' : '-'}${fEur(d.value)} ${d.label}`}
                </Typography>
                {d.hint && (
                  <Tooltip title={d.hint}>
                    <span style={{ display: 'inline-flex' }}>
                      <Iconify
                        icon="eva:info-outline"
                        width={13}
                        height={13}
                        sx={{ color: 'text.disabled', cursor: 'help' }}
                      />
                    </span>
                  </Tooltip>
                )}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Card>
  );
}

KpiCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  hint: PropTypes.string,
  icon: PropTypes.string.isRequired,
  color: PropTypes.string,
  deltas: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      direction: PropTypes.oneOf(['up', 'down']),
      label: PropTypes.string,
      hint: PropTypes.string,
    })
  ),
};

export default function SubscriptionsKpis({ stats }) {
  if (!stats) return null;

  const monthlyBilling = Number(stats.monthlyBilling) || 0;

  // Horizonte de renewalsByMonth = 12 meses → cada anual activa aparece 1 vez,
  // así que la suma de `total` es la facturación anual total de las anuales activas.
  const annualBillingTotal = (stats.renewalsByMonth || []).reduce((sum, m) => sum + (Number(m?.total) || 0), 0);

  const theoreticalMrr = monthlyBilling + annualBillingTotal / 12;
  const arr = theoreticalMrr * 12;

  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const currentMonthRenewals =
    Number((stats.renewalsByMonth || []).find((m) => m?.month === currentMonthKey)?.total) || 0;
  // Las nuevas suscripciones anuales del mes no están en renewalsByMonth (su renovación cae
  // dentro de un año), pero su primer cobro entra en caja este mes — así que suman aquí.
  const newAnnualThisMonth = Number(stats.newAnnualSubscriptionsThisMonth?.totalEUR) || 0;
  const currentMonthBilling = monthlyBilling + currentMonthRenewals + newAnnualThisMonth;

  // MRR recurrente nuevo aportado por las mensuales dadas de alta este mes. Ya está dentro
  // de `monthlyBilling`; se muestra como delta sobre el "MRR real", no se vuelve a sumar.
  const newMonthlyMrr = Number(stats.newMonthlySubscriptionsThisMonth?.mrrAddedEUR) || 0;
  // MRR de las mensuales con cancelación programada — el que se irá al cierre de su periodo.
  // Sigue dentro de `monthlyBilling` hasta que la baja se hace efectiva.
  const lostMonthlyMrr = (stats.canceledMonthly || []).reduce((sum, c) => sum + (Number(c?.cycleAmountEUR) || 0), 0);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <KpiCard
          label="Facturación del mes"
          value={fEur(currentMonthBilling)}
          icon="eva:pie-chart-2-outline"
          color="warning"
          hint="MRR real + renovaciones anuales que entran este mes + primer cobro de las nuevas suscripciones anuales del mes. Es la caja recurrente que entra por Stripe en el mes en curso."
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KpiCard
          label="MRR real"
          value={fEur(monthlyBilling)}
          icon="eva:credit-card-outline"
          color="primary"
          deltas={[
            {
              value: newMonthlyMrr,
              direction: 'up',
              label: 'este mes',
              hint: 'MRR recurrente nuevo aportado por las suscripciones mensuales dadas de alta este mes. Ya está incluido en el MRR real.',
            },
            {
              value: lostMonthlyMrr,
              direction: 'down',
              label: 'en bajas',
              hint: 'MRR de las suscripciones mensuales con cancelación programada — se irá al cierre de su periodo. Todavía está incluido en el MRR real.',
            },
          ].filter((d) => d.value > 0)}
          hint="Suma de las suscripciones mensuales activas en EUR (stats.monthlyBilling). No incluye anuales."
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KpiCard
          label="MRR teórico"
          value={fEur(theoreticalMrr)}
          icon="eva:trending-up-outline"
          color="info"
          hint="MRR real + facturación anual de las suscripciones anuales activas / 12. Es la métrica SaaS estándar (todo normalizado a base mensual)."
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KpiCard
          label="ARR"
          value={fEur(arr)}
          icon="eva:calendar-outline"
          color="success"
          hint="MRR teórico × 12. Ingresos recurrentes anualizados con la base actual."
        />
      </Grid>
    </Grid>
  );
}

SubscriptionsKpis.propTypes = {
  stats: PropTypes.object,
};
